import { useRef } from 'react';

import { type OperationContext } from 'urql';

import { useIsFocused } from '@sg/garnish';

/**
 * Returns a query context whose identity changes only when the screen regains
 * focus after the refresh window. urql re-runs a query when its context changes,
 * so the menu refreshes on focus at most once per window.
 */
export const useHomeMenuRefreshContext = () => {
  const isFocused = useIsFocused();

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const refreshStateRef = useRef<RefreshState | undefined>(undefined);

  // ─── Derived ───────────────────────────────────────────────────────────────

  refreshStateRef.current = getNextRefreshState({
    refreshState: refreshStateRef.current,
    isFocused,
  });

  return refreshStateRef.current.context;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getNextRefreshState(params: GetNextRefreshStateParams): RefreshState {
  const { refreshState, isFocused } = params;

  if (!refreshState) {
    return createRefreshState(isFocused);
  }

  const hasFocusChanged = isFocused !== refreshState.isFocused;

  if (!hasFocusChanged) {
    return refreshState;
  }

  const hasRefreshWindowElapsed = Date.now() - refreshState.refreshedAt >= REFRESH_WINDOW_MS;
  const shouldRefresh = isFocused && hasRefreshWindowElapsed;

  if (!shouldRefresh) {
    return { ...refreshState, isFocused };
  }

  return createRefreshState(isFocused);
}

function createRefreshState(isFocused: boolean): RefreshState {
  return { isFocused, refreshedAt: Date.now(), context: {} };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const REFRESH_WINDOW_MS = 5 * 60 * 1000;

// ─── Types ───────────────────────────────────────────────────────────────────

type GetNextRefreshStateParams = {
  refreshState: RefreshState | undefined;
  isFocused: boolean;
};

type RefreshState = {
  isFocused: boolean;
  refreshedAt: number;
  context: Partial<OperationContext>;
};
